import { useLingui, Trans } from '@lingui/react/macro';
import { Badge, Button, toast } from '@utima/ui';
import { Pencil, Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';

import { trpc, type RouterOutputs } from '@/services/trpc';

import { SkillDialog } from './SkillDialog';
import { useSkillMutation } from '../hooks/useSkillMutation';

type SkillRow = RouterOutputs['skills']['findAll'][number];

export function SkillsTab() {
  const { t } = useLingui();
  const { data: skills, isLoading } = trpc.skills.findAll.useQuery();
  const { remove } = useSkillMutation();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<SkillRow | undefined>();

  const openCreate = () => {
    setEditing(undefined);
    setDialogOpen(true);
  };

  const openEdit = (skill: SkillRow) => {
    setEditing(skill);
    setDialogOpen(true);
  };

  const handleDelete = async (skill: SkillRow) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t`Delete skill "${skill.name}"?`)) {
      return;
    }

    await remove.mutateAsync({ id: skill.id });
    toast.success(t`Skill deleted.`);
  };

  const items = skills ?? [];

  return (
    <div className='space-y-4'>
      <div className='flex items-center justify-between'>
        <span className='text-sm font-medium'>
          <Trans>Your skills</Trans>
        </span>
        <Button size='sm' onClick={openCreate}>
          <Plus className='size-4' />
          <Trans>Add skill</Trans>
        </Button>
      </div>

      {isLoading && (
        <p className='text-sm text-muted-fg'>
          <Trans>Loading…</Trans>
        </p>
      )}

      {!isLoading && items.length === 0 && (
        <div className='rounded-radius border border-dashed border-border p-8 text-center text-sm text-muted-fg'>
          <Trans>
            No skills yet. Skills are on-demand instructions the agent loads
            when a request matches — keep the base prompt small and put
            procedures here.
          </Trans>
        </div>
      )}

      {items.length > 0 && (
        <div className='overflow-hidden rounded-radius border border-border'>
          <table className='w-full text-sm'>
            <thead className='bg-muted/40 text-left text-muted-fg'>
              <tr>
                <th className='p-3 font-medium'>
                  <Trans>Name</Trans>
                </th>
                <th className='p-3 font-medium'>
                  <Trans>Description</Trans>
                </th>
                <th className='p-3 font-medium'>
                  <Trans>Scope</Trans>
                </th>
                <th className='p-3 text-right font-medium'>
                  <Trans>Actions</Trans>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map(skill => (
                <tr key={skill.id} className='border-t border-border'>
                  <td className='p-3'>
                    <div className='font-medium'>{skill.name}</div>
                    {!skill.isActive && (
                      <Badge variant='muted' size='xs'>
                        <Trans>Inactive</Trans>
                      </Badge>
                    )}
                  </td>
                  <td className='max-w-96 truncate p-3 text-muted-fg'>
                    {skill.description}
                  </td>
                  <td className='p-3'>
                    {skill.availableToAllChats ? (
                      <Trans>All chats</Trans>
                    ) : (
                      <Trans>{skill.chatIds?.length ?? 0} chats</Trans>
                    )}
                  </td>
                  <td className='p-3'>
                    <div className='flex justify-end gap-1'>
                      <Button
                        size='sm'
                        variant='ghost'
                        title={t`Edit`}
                        onClick={() => openEdit(skill)}
                      >
                        <Pencil className='size-4' />
                      </Button>
                      <Button
                        size='sm'
                        variant='ghost'
                        title={t`Delete`}
                        onClick={() => handleDelete(skill)}
                      >
                        <Trash2 className='size-4 text-danger' />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <SkillDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        skill={editing}
      />
    </div>
  );
}
